<template>
    <v-dialog v-model="showDialog" max-width="900px" scrollable persistent>
        <v-card>
            <v-card-title class="headline primary_2 white--text">
                Edit Eligibility Details
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-6">
                <v-form ref="productForm" v-model="valid">
                    <div class="text-h6 mb-4">Eligibility Details</div>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="eligibility_details.product_type"
                                :items="productTypes"
                                item-text="loan_type"
                                item-value="id"
                                label="Product Type*"
                                :rules="requiredArrayRule"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="eligibility_details.cibil_score"
                                label="Minimum CIBIL Score*"
                                :rules="$rules.REQUIRED_FIELD"
                                type="number"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="eligibility_details.minimum_age"
                                label="Minimum Age*"
                                :rules="$rules.REQUIRED_FIELD"
                                type="number"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="eligibility_details.maximum_age"
                                label="Maximum Age*"
                                :rules="$rules.REQUIRED_FIELD"
                                type="number"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="eligibility_details.minimum_income"
                                label="Minimum Income*"
                                :rules="requiredIncomeRules"
                                type="number"
                                prefix="₹"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="eligibility_details.maximum_income"
                                label="Maximum Income"
                                type="number"
                                prefix="₹"
                                :rules="incomeRule"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="eligibility_details.employment_type"
                                :items="employmentTypes"
                                label="Employment Type*"
                                :rules="requiredArrayRule"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="eligibility_details.salary_type"
                                :items="salaryTypes"
                                label="Salary Type*"
                                :rules="requiredArrayRule"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="includedPincodeInput"
                                label="Included Pincodes"
                                outlined
                                dense
                                maxlength="6"
                                hint="Type 6-digit pincode and press Enter"
                                persistent-hint
                                @input="includedPincodeInput = normalizePincode(includedPincodeInput)"
                                @keydown.enter.prevent="addIncludedPincode"
                            ></v-text-field>
                            <div v-if="eligibility_details.pincode_list.length" class="mt-2">
                                <v-chip
                                    v-for="(pincode, index) in eligibility_details.pincode_list"
                                    :key="'included-' + pincode + index"
                                    class="ma-1"
                                    close
                                    small
                                    @click:close="removeIncludedPincode(index)"
                                >
                                    {{ pincode }}
                                </v-chip>
                            </div>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="excludedPincodeInput"
                                label="Excluded Pincodes"
                                outlined
                                dense
                                maxlength="6"
                                hint="Type 6-digit pincode and press Enter"
                                persistent-hint
                                @input="excludedPincodeInput = normalizePincode(excludedPincodeInput)"
                                @keydown.enter.prevent="addExcludedPincode"
                            ></v-text-field>
                            <div v-if="eligibility_details.excluded_pincode_list.length" class="mt-2">
                                <v-chip
                                    v-for="(pincode, index) in eligibility_details.excluded_pincode_list"
                                    :key="'excluded-' + pincode + index"
                                    class="ma-1"
                                    close
                                    small
                                    @click:close="removeExcludedPincode(index)"
                                >
                                    {{ pincode }}
                                </v-chip>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text color="error" @click="closeDialog">Cancel</v-btn>
                <v-btn color="success" @click="submitForm" :disabled="!valid || isSubmitting" :loading="isSubmitting">
                    Edit Eligibility
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    data() {
        return {
            showDialog: false,
            valid: false,
            isSubmitting: false,
            eligibilityId: null,
            includedPincodeInput: "",
            excludedPincodeInput: "",
            requiredArrayRule: [
                (value) =>
                    (Array.isArray(value) && value.length > 0) || "This field is required",
            ],
            incomeRule: [
                (value) =>
                    !value ||
                    /^\d{1,8}(\.\d{1,2})?$/.test(String(value)) ||
                    "Maximum 8 digits allowed before decimal",
            ],
            requiredIncomeRules: [
                (value) => !!value || "This field is required",
                (value) =>
                    /^\d{1,8}(\.\d{1,2})?$/.test(String(value)) ||
                    "Maximum 8 digits allowed before decimal",
            ],
            eligibility_details: this.getEmptyEligibility(),
            productTypes: [],
            employmentTypes: [
                'Salaried',
                'Self Employed',
                'Unemployed'
            ],
            salaryTypes: [
                'Direct Account Transfer',
                'Cash',
                'Cheque'
            ],
        }
    },
    created() {
        this.getProductTypes();
    },
    methods: {
        getEmptyEligibility() {
            return {
                product_type: [],
                minimum_age: 21,
                maximum_age: 65,
                minimum_income: null,
                maximum_income: null,
                employment_type: [],
                salary_type: [],
                cibil_score: 650,
                pincode_list: [],
                excluded_pincode_list: []
            };
        },
        toArray(value) {
            if (!value) {
                return [];
            }
            if (Array.isArray(value)) {
                return value.filter(Boolean);
            }
            if (typeof value === "string") {
                return value.split(",").map((item) => item.trim()).filter(Boolean);
            }
            return [value];
        },
        openDialog(item) {
            this.eligibilityId = item.id;
            this.eligibility_details = {
                product_type: item.product_type ? [item.product_type] : [],
                minimum_age: item.minimum_age,
                maximum_age: item.maximum_age,
                minimum_income: item.minimum_income,
                maximum_income: item.maximum_income,
                employment_type: this.toArray(item.employment_type),
                salary_type: this.toArray(item.salary_type),
                cibil_score: item.cibil_score,
                pincode_list: this.toArray(item.pincode_list),
                excluded_pincode_list: this.toArray(item.excluded_pincode_list),
            };
            this.includedPincodeInput = "";
            this.excludedPincodeInput = "";
            this.showDialog = true;
            this.$nextTick(() => {
                if (this.$refs.productForm) {
                    this.$refs.productForm.resetValidation();
                }
            });
        },
        closeDialog() {
            this.showDialog = false;
            this.resetForm();
        },
        normalizePincode(value) {
            return String(value || "").replace(/\D/g, "").slice(0, 6);
        },
        addPincodeToList(inputValue, listKey, inputKey) {
            const pincode = this.normalizePincode(inputValue);
            if (pincode.length !== 6) {
                this.showSnakeBar("error", "Please enter a valid 6-digit pincode");
                return;
            }
            const list = this.eligibility_details[listKey];
            if (!list.includes(pincode)) {
                list.push(pincode);
            }
            this[inputKey] = "";
        },
        addIncludedPincode() {
            this.addPincodeToList(
                this.includedPincodeInput,
                "pincode_list",
                "includedPincodeInput"
            );
        },
        addExcludedPincode() {
            this.addPincodeToList(
                this.excludedPincodeInput,
                "excluded_pincode_list",
                "excludedPincodeInput"
            );
        },
        removeIncludedPincode(index) {
            this.eligibility_details.pincode_list.splice(index, 1);
        },
        removeExcludedPincode(index) {
            this.eligibility_details.excluded_pincode_list.splice(index, 1);
        },
        resetForm() {
            this.eligibilityId = null;
            this.includedPincodeInput = "";
            this.excludedPincodeInput = "";
            this.eligibility_details = this.getEmptyEligibility();
            if (this.$refs.productForm) {
                this.$refs.productForm.resetValidation();
            }
        },
        getProductTypes() {
            const self = this;

            const params = { flag_active: false };

            const successHandler = (response) => {
                self.productTypes = response.data.result;
            };

            self.request_GET(
                self,
                self.$urls.GET_LOAN_TYPE,
                params,
                successHandler,
                null,
                null
            );
        },

        submitForm() {
            const self = this;
            if (!this.$refs.productForm.validate() || !this.eligibilityId) {
                return;
            }

            const productTypeIds = this.toArray(this.eligibility_details.product_type);
            const formData = {
                product_type: productTypeIds[0],
                minimum_age: Number(this.eligibility_details.minimum_age),
                maximum_age: Number(this.eligibility_details.maximum_age),
                minimum_income: this.eligibility_details.minimum_income,
                maximum_income: this.eligibility_details.maximum_income,
                employment_type: this.toArray(this.eligibility_details.employment_type),
                salary_type: this.toArray(this.eligibility_details.salary_type),
                cibil_score: Number(this.eligibility_details.cibil_score),
                pincode_list: this.eligibility_details.pincode_list || [],
                excluded_pincode_list: this.eligibility_details.excluded_pincode_list || [],
            };

            self.isSubmitting = true;

            const successHandler = () => {
                self.showSnakeBar("success", "Eligibility updated successfully");
                self.closeDialog();
                self.$emit("reload");
            };

            const errorHandler = (error) => {
                const message =
                    self.getApiErrorMessage(error) || "Failed to update eligibility";
                self.showSnakeBar("error", message);
            };

            const finallyHandler = () => {
                self.isSubmitting = false;
            };

            self.request_PATCH(
                self,
                self.$urls.UPDATE_PRODUCT_ELIGIBILITY_DETAILS,
                `${this.eligibilityId}/`,
                formData,
                successHandler,
                null,
                errorHandler,
                finallyHandler
            );
        }
    }
}
</script>
